import React from "react";
import PropTypes from "prop-types";
import moment from "moment/moment";
import { Spinner } from "../../components";
import { CoreChart } from "./core_chart";
import { ROW_TYPE } from "../_utils/constants";

// const markerArray = [
//   "circle",
//   "square",
//   "triangle",
//   "triangle-down",
//   "diamond"
// ];
const dashArray = [
  "Solid",
  "Dash",
  "Dot",
  "DashDot",
  "ShortDash",
  "ShortDashDotDot",
  "LongDash",
  "LongDashDot",
  "LongDashDotDot",
  "ShortDot",
  "ShortDashDot"
];
const colorArray = [
  "rgb(88, 153, 218)",
  "rgb(232, 116, 59)",
  "rgb(25, 169, 121)",
  "rgb(237, 74, 123)",
  "rgb(148, 94, 207)",
  "rgb(19, 164, 180)",
  "rgb(82, 93, 244)",
  "rgb(191, 57, 158)",
  "rgb(108, 136, 147)",
  "rgb(238, 104, 104)",
  "rgb(47, 100, 151)"
];
const CoreReportChart = ({
  className,
  chartWidth,
  chartHeight,
  chartTitle,
  data,
  dataMode,
  loading,
  theme,
  showLegend,
  isTooltipSplit
}) => {
  let _axes = {};
  //  let legend = {};

  function getSeriesData(row) {
    let seriesData = [];
    const refRowIndex = row.refRowIndex;
    const refColKey = row.refColKey;

    let min;
    let max;
    let minDtUnix;
    let maxDtUnix;

    if (data && data[refRowIndex] && data[refRowIndex].data) {
      const objData = data[refRowIndex].data;
      Object.keys(objData).forEach((dt, i) => {
        const value = objData[dt][refColKey];
        const dtUtcUnix = moment
          .utc(dt.toString(), "YYYY-MM-DDTHH:mm:ss")
          .unix();

        if (i === 0) {
          min = max = value;
          minDtUnix = maxDtUnix = dtUtcUnix;
        } else {
          if (value < min) min = value;
          if (value > max) max = value;
          if (dtUtcUnix < minDtUnix) minDtUnix = dtUtcUnix;
          if (dtUtcUnix > maxDtUnix) maxDtUnix = dtUtcUnix;
        }

        seriesData.push([dtUtcUnix * 1000, value]); //convert dtunix to ms
      });
    }

    return {
      data: seriesData,
      min: min,
      max: max,
      minDtUnix: minDtUnix,
      maxDtUnix: maxDtUnix
    };
  }

  function getSeriesName(row, getShortName) {
    const refRowIndex = row.refRowIndex;
    return getShortName ? row.text : `${row.text} (${data[refRowIndex].text})`; //append refrowname
  }

  function getSeriesAxisLabel(row) {
    return `${row.text} (${row.units})`;
  }

  function getSeriesYAxis(row) {
    return row.axisType;
  }

  function getSeriesRange(min, max, center, roundingDecimalPlaces) {
    const lowerDiff = Math.abs(center - min);
    const upperDiff = Math.abs(center - max);
    const maxDiff = Math.max(lowerDiff, upperDiff);
    const lowerRange = parseFloat(
      (center - maxDiff).toFixed(roundingDecimalPlaces)
    );
    const upperRange = parseFloat(
      (center + maxDiff).toFixed(roundingDecimalPlaces)
    );

    return { lowerRange, upperRange };
  }

  function getDateTimeFromUtcUnix(dtUtcUnix) {
    if (dtUtcUnix)
      return moment
        .unix(dtUtcUnix)
        .utc()
        .format("DD/MM/YYYY HH:mm:ss");
    return "";
  }

  // function getSeriesType(row) {
  //   if (row.dataMode === "5m" && dataMode === "4s")
  //     //if variable is 5m, but mode is 4s, plot as stepped area
  //     return "area";
  //   //default
  //   return "line";
  // }
  //
  // function isStepped(row) {
  //   return row.dataMode === "5m" && dataMode === "4s"; //if variable is 5m, but mode is 4s, plot as stepped area
  // }

  let series = [];
  let chartMinDtUnix;
  let chartMaxDtUnix;
  if (data) {
    //console.log("CoreReportChart data", data);
    let colorCounter = 0;
    let variableUniqIndexer = {};

    let groupModes = {};
    //calculate number of elements in each group
    data.filter(row => row.rowType === ROW_TYPE.OBJECT).forEach(row => {
      const groupType = row.groupType;
      if (!groupModes[groupType]) groupModes[groupType] = 0;
      groupModes[groupType]++;
    });

    let isMultiElementMode = false;

    data.forEach(row => {
      if (row.rowType === ROW_TYPE.OBJECT) {
        isMultiElementMode = groupModes[row.groupType] > 1;
      }

      if (row.rowType === ROW_TYPE.VARIABLE) {
        const elementMode = isMultiElementMode ? "multiple" : "single";
        const matchingRules =
          theme &&
          theme.rules &&
          theme.rules.filter(
            r =>
              (r.variable ? r.variable === row.value : true) &&
              (r.elementMode ? r.elementMode === elementMode : true)
          );
        let rule = {};
        if (matchingRules && matchingRules.length > 0) {
          rule = matchingRules[0]; //only single rule for now,
        }

        let formatting = rule && rule.formatting;

        const variableName = row.text;
        if (variableUniqIndexer[variableName] === undefined)
          variableUniqIndexer[variableName] = Object.keys(
            variableUniqIndexer
          ).length;

        const name = getSeriesName(row);
        // const shortName = getSeriesName(row, true);
        const axisLabel = getSeriesAxisLabel(row);
        const seriesData = getSeriesData(row);

        if (!chartMinDtUnix || seriesData.minDtUnix < chartMinDtUnix)
          chartMinDtUnix = seriesData.minDtUnix;
        if (!chartMaxDtUnix || seriesData.maxDtUnix > chartMaxDtUnix)
          chartMaxDtUnix = seriesData.maxDtUnix;

        let yAxis = rule.axisType ? rule.axisType : getSeriesYAxis(row); //get axis from theme or default of variable

        if (!_axes[yAxis])
          _axes[yAxis] = {
            id: yAxis,
            //title: { text: "" },
            series: [],
            min: null,
            max: null
          };

        //set axis labels
        if (_axes[yAxis].series.indexOf(axisLabel) < 0)
          _axes[yAxis].series.push(axisLabel); //names for axis label, if already not added

        //set axis to left or right side
        _axes[yAxis].opposite = yAxis.indexOf("right") >= 0; //default will be left

        //set min value for series as zero or floating
        if (yAxis.indexOf("min") >= 0) {
          if (yAxis.indexOf("zero") >= 0) {
            //set origin to zero
            _axes[yAxis].min = 0;
            //set max as otherwise chart is behaving weird
            if (
              _axes[yAxis].max === null ||
              (seriesData.max > _axes[yAxis].max && seriesData.max !== null)
            ) {
              _axes[yAxis].max = seriesData.max;
            }
          } else if (yAxis.indexOf("floating")) {
            //set origin to min of all series sharing this axis i.e. floating
            if (
              _axes[yAxis].min === null ||
              (seriesData.min < _axes[yAxis].min && seriesData.min !== null)
            )
              _axes[yAxis].min = seriesData.min;

            //set max as otherwise chart is behaving weird
            if (
              _axes[yAxis].max === null ||
              seriesData.max > _axes[yAxis].max
            ) {
              _axes[yAxis].max = seriesData.max;
            }
          } else {
            console.error("Not implemented min type for axis:", yAxis);
          }
        }

        //set min and max for series to center them at 0 or fifty
        if (yAxis.indexOf("center") >= 0) {
          const centerValue = yAxis.indexOf("fifty") >= 0 ? 50 : 0;

          let hasChanged = false;
          if (
            _axes[yAxis].min === null ||
            (seriesData.min < _axes[yAxis].min && seriesData.min !== null)
          ) {
            _axes[yAxis].min = seriesData.min;
            hasChanged = true;
          }

          if (
            _axes[yAxis].max === null ||
            (seriesData.max > _axes[yAxis].max && seriesData.max !== null)
          ) {
            _axes[yAxis].max = seriesData.max;
            hasChanged = true;
          }

          if (hasChanged) {
            const range = getSeriesRange(
              _axes[yAxis].min,
              _axes[yAxis].max,
              centerValue,
              row.decimalPlaces
            );

            _axes[yAxis].min = range.lowerRange;
            _axes[yAxis].max = range.upperRange;
          }
          if (centerValue === 0) {
            //add zero plot lines when centering at zero
            _axes[yAxis].plotLines = [
              {
                color: "#555",
                dashStyle: "dash",
                width: 1,
                value: 0,
                zIndex: 100
              }
            ];
          }
        }

        series.push({
          showInNavigator: true,
          name: name,
          data: seriesData.data,
          yAxis: yAxis,
          type: "line",
          tooltip: {
            valueDecimals: row.decimalPlaces
          },
          //colorIndex: objectCounter,
          color: colorArray[colorCounter],
          dashStyle: isMultiElementMode
            ? dashArray[variableUniqIndexer[variableName]]
            : dashArray[0],
          ...formatting

          // marker: {
          //   enabled: true,
          //   radius: 4,
          //   symbol: markerArray[variableUniqIndexer[variableName]],
          //   //enabledThreshold: 4000
          //   //   fillColor: "#333"
          // }
          // ...row.seriesOptions, //overrides,
          // ...row[`seriesOptions-${dataMode}`] //overrides based on datamode
        });
      }

      if (isMultiElementMode) {
        if (row.rowType === ROW_TYPE.OBJECT) colorCounter++;
      } else colorCounter++;
    });
  }

  //convert to array
  let yAxes = [];
  Object.keys(_axes).forEach(i => {
    yAxes.push({
      ..._axes[i],
      title: { text: showLegend ? _axes[i].series.join(", ") : "" }
    });
  });
  // console.log("core report chart", data, series, _axes, yAxes);
  return (
    <div className={className} style={{ position: "relative" }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            zIndex: 10,
            width: "100%",
            height: "100%",
            background: "#ffffffd4"
          }}
        >
          <Spinner style={{ position: "absolute", top: "49%", left: "49%" }} />
        </div>
      )}
      <CoreChart
        chartWidth={chartWidth}
        chartHeight={chartHeight}
        series={series}
        chartTitle={chartTitle}
        //chartSubTitle={dataMode === "4s" ? "4 seconds data" : "5 minutes data"}
        chartSubTitle={
          //chartMinDtUnix && chartMaxDtUnix
          true
            ? `${getDateTimeFromUtcUnix(
                chartMinDtUnix
              )} - ${getDateTimeFromUtcUnix(chartMaxDtUnix)}`
            : ""
        }
        xAxisLabel={
          showLegend ? (dataMode === "4s" ? "time4sec" : "settlement date") : ""
        }
        yAxes={yAxes}
        //legend={legend}
        showLegend={showLegend}
        isTooltipSplit={isTooltipSplit}
      />
    </div>
  );
};

CoreReportChart.propTypes = {
  className: PropTypes.string,
  chartWidth: PropTypes.number,
  chartHeight: PropTypes.number,
  chartTitle: PropTypes.string,
  data: PropTypes.array.isRequired,
  dataMode: PropTypes.string.isRequired,
  showLegend: PropTypes.bool.isRequired,
  isTooltipSplit: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  theme: PropTypes.object
};

export default CoreReportChart;



// WEBPACK FOOTER //
// ./src/views/core/core_report_chart/CoreReportChart.jsx